import React, { useState } from 'react'
import { LEGACY_REACTIONS } from '../../constants/reactions'
import ReactionIcon from './ReactionIcon'
import MemberNameLink from '../common/MemberNameLink'

/**
 * FamilyReactionsGathered — shows who gathered around this content.
 *
 * Groups reactions by type (in fixed order), showing reactor names
 * and optional notes. Never shows counts as numbers.
 *
 * Design: "Gathered, not counted."
 */
function FamilyReactionsGathered({ grouped = {} }) {
  const [expandedType, setExpandedType] = useState(null)

  // Only show types that have at least one reaction
  const activeTypes = LEGACY_REACTIONS.filter(
    (r) => grouped[r.type] && grouped[r.type].length > 0
  )

  if (activeTypes.length === 0) return null

  // Count unique reactors across all types
  const uniqueReactorIds = new Set()
  Object.values(grouped).forEach((typeReactions) => {
    typeReactions.forEach((r) => {
      if (r.reactor?.id) uniqueReactorIds.add(r.reactor.id)
    })
  })

  return (
    <div className="family-gathered">
      <p className="family-gathered-label">
        Family gathered around this
      </p>
      <div className="family-gathered-groups">
        {activeTypes.map((reactionDef) => {
          const typeReactions = grouped[reactionDef.type] || []
          const isExpanded = expandedType === reactionDef.type

          return (
            <div
              key={reactionDef.type}
              className={`gathered-group ${isExpanded ? 'expanded' : ''}`}
              style={{
                '--group-color': reactionDef.color,
                '--group-color-light': reactionDef.colorLight,
              }}
            >
              <button
                className="gathered-group-header"
                onClick={() =>
                  setExpandedType(isExpanded ? null : reactionDef.type)
                }
              >
                <ReactionIcon
                  type={reactionDef.type}
                  state="active"
                  size={16}
                />
                <span className="gathered-group-label">
                  {reactionDef.label}
                </span>
                <div className="gathered-avatar-stack">
                  {typeReactions.slice(0, 3).map((reaction, idx) => (
                    <div
                      key={reaction.id}
                      className="gathered-avatar-mini"
                      style={{
                        backgroundColor: reaction.reactor?.avatarColor || '#6B7F4A',
                        zIndex: 3 - idx,
                      }}
                      title={`${reaction.reactor?.firstName || ''} ${reaction.reactor?.lastName || ''}`.trim()}
                    >
                      {reaction.reactor?.initials || '?'}
                    </div>
                  ))}
                  {typeReactions.length > 3 && (
                    <span className="gathered-more">
                      +{typeReactions.length - 3}
                    </span>
                  )}
                </div>
              </button>

              {/* Expanded view: full names and notes */}
              {isExpanded && (
                <div className="gathered-group-people">
                  {typeReactions.map((reaction) => (
                    <div key={reaction.id} className="gathered-person">
                      <div
                        className="gathered-avatar"
                        style={{
                          backgroundColor:
                            reaction.reactor?.avatarColor || '#6B7F4A',
                        }}
                      >
                        {reaction.reactor?.initials || '?'}
                      </div>
                      <div className="gathered-person-info">
                        <span className="gathered-name">
                          <MemberNameLink memberId={reaction.reactor?.id} name={`${reaction.reactor?.firstName || 'Someone'} ${reaction.reactor?.lastName || ''}`.trim()} />
                        </span>
                        {reaction.timeAgo && (
                          <span className="gathered-time">
                            {reaction.timeAgo}
                          </span>
                        )}
                        {reaction.note && (
                          <p className="gathered-note">
                            &ldquo;{reaction.note}&rdquo;
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FamilyReactionsGathered
